import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { formatPrice, formatAmount } from "../../utils/functions.js";
import placeholder from "../../assets/placeholder.svg";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  DollarSign,
  X,
  Plus,
  Check,
  XCircle,
  Upload,
} from "lucide-react";
import PaymentPopup from "../common/PaymentPopup";

const BuyerPayment = () => {
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, paid, pending
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicesDetails, setInvoicesDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
//mondy fix receipt show 
  const [showReceiptsModal, setShowReceiptsModal] = useState(false);
  const [selectedReceipts, setSelectedReceipts] = useState([]);
  //mondy fix end

  // Fetch buyer invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get("/buyerinvoices/myinvoices");

        // Handle different response structures
        const invoicesData = response.data.data || [];

        console.log("API response data:", invoicesData);

        if (!Array.isArray(invoicesData)) {
          console.error("API response data is not an array:", invoicesData);
          setInvoices([]);
          return;
        }

        setInvoices(invoicesData);
      } catch (error) {
        console.error("Error fetching buyer invoices:", error);
        setError(translate("buyerInvoices.messages.noInvoicesFound"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchInvoices();
    }
  }, [user?.id]);

  // useEffect(() => {
  //   fetchInvoicesDetails(invoices?.[0]?.id);
  // }, [invoices]);

  // const fetchInvoicesDetails = async (invoiceId) => {
  //   const response = await api.get(`/buyerinvoicepayments/mypayments/${invoiceId}`);
  //   setInvoicesDetails(response.data.data);
  //   console.log(response.data.data);
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUnderApprovalAmount = (invoice) => {
    const payments = invoice.buyerInvoicePayments || [];
    return payments.reduce((sum, payment) => {
      const amount = Number(payment?.amountPaid || 0);
      if (payment?.isApproved === null && amount > 0) {
        return sum + amount;
      }
      return sum;
    }, 0);
  };

  const getInvoiceStatus = (invoice) => {
    const status =
      invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

    const statusConfig = {
      Paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      Pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      "Partial Payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-orange-700",
      },
      "partial-payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-orange-700",
      },
      Overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      Approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      Rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
      rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
      cancelled: {
        text: translate("buyerInvoices.status.cancelled"),
        color: "text-white bg-red-500",
      },
      "Under Approval": {
        text:
          translate("buyerInvoices.status.underApproval") || "Under Approval",
        color: "text-white bg-blue-500",
      },
    };

    // Check if there's a payment waiting for approval (amountPaid > 0 and isApproved === null)
    const isUnderApproval = (invoice.buyerInvoicePayments || []).some(
      (payment) => payment.amountPaid > 0 && payment.isApproved === null,
    );

    if (
      isUnderApproval &&
      (status === "Pending" ||
        status === "pending" ||
        status === "Partial Payment" ||
        status === "partial-payment")
    ) {
      return statusConfig["Under Approval"];
    }

    return (
      statusConfig[status] || {
        text: translate("buyerInvoices.status.unknown"),
        color: "text-white bg-gray-700",
      }
    );
  };

  const filteredInvoices = invoices
    .filter((invoice) => {
      // Filter by status
      if (filterStatus !== "all") {
        const status =
          invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

        // Map filter status to actual status values
        if (filterStatus === "paid") {
          if (
            !(
              status === "Paid" ||
              status === "paid" ||
              status === "Approved" ||
              status === "approved"
            )
          ) {
            return false;
          }
        } else if (filterStatus === "pending") {
          const isUnderApproval = (invoice.buyerInvoicePayments || []).some(
            (p) => p.amountPaid > 0 && p.isApproved === null,
          );
          if (
            !(status === "Pending" || status === "pending") ||
            isUnderApproval
          ) {
            return false;
          }
        } else if (filterStatus === "partial-payment") {
          const isUnderApproval = (invoice.buyerInvoicePayments || []).some(
            (p) => p.amountPaid > 0 && p.isApproved === null,
          );
          if (
            !(status === "Partial Payment" || status === "partial-payment") ||
            isUnderApproval
          ) {
            return false;
          }
        } else if (filterStatus === "under-approval") {
          const isUnderApproval = (invoice.buyerInvoicePayments || []).some(
            (p) => p.amountPaid > 0 && p.isApproved === null,
          );
          if (!isUnderApproval) {
            return false;
          }
        } else if (filterStatus === "cancelled") {
          if (!(status === "Cancelled" || status === "cancelled")) {
            return false;
          }
        } else if (status !== filterStatus) {
          return false;
        }
      }

      // Filter by invoice number search
      if (searchInvoiceNumber && searchInvoiceNumber.trim() !== "") {
        const invoiceNumber = invoice?.id?.toString() || "";
        return invoiceNumber
          .toLowerCase()
          .includes(searchInvoiceNumber.toLowerCase().trim());
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by updatedAt descending (newest first)
      const aDate = new Date(a?.updatedAt || 0);
      const bDate = new Date(b?.updatedAt || 0);

      return bDate - aDate; // Descending order (newest first)
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchInvoiceNumber]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCloseModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };
//mondy fix receipt show 
  const handleViewReceipts = (invoice) => {
    setSelectedReceipts(invoice?.buyerInvoicePayments || []);
    setShowReceiptsModal(true);
  };
  const closeReceiptsModal = () => {
    setShowReceiptsModal(false);
    setSelectedReceipts([]);
  };

const canViewReceipts = (invoice) => {
  const status =
    invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

  const payments = invoice.buyerInvoicePayments || [];

  const hasValidPayment = payments.some(
    (p) => Number(p.amountPaid || 0) > 0
  );

  const hasAnyPaymentRecord = payments.length > 0;

  const isPending =
    status === "Pending" || status === "pending";

  // ❌ If no payments at all → never show
  if (!hasAnyPaymentRecord) return false;

  // ❌ If no amount paid → never show
  if (!hasValidPayment) return false;

  // ❌ Special rule:
  // If Pending AND payment exists → still show
  if (isPending && hasValidPayment) return true;

  // ✅ For all other statuses → show if payment exists
  return hasValidPayment;
};
// mondy fix end 

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-[#f64f2d]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-4 text-sm sm:text-base">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 text-sm sm:text-base"
        >
          {translate("buyerInvoices.buttons.close")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-[#FFF9F9] rounded-lg sm:rounded-xl shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-lg sm:rounded-t-[16px] px-1 py-1">
        <div className="p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold">
            {translate("buyerInvoices.heading")}
          </h2>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4">
        {/* Filter Navigation */}
        <div className="border-b border-gray-200">
          {/* Filter Tabs and Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4">
            {/* Filter Tabs */}
            <nav className="flex space-x-1.5 sm:space-x-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "all"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.all")} ({invoices.length})
              </button>
              <button
                onClick={() => setFilterStatus("paid")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "paid"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.paid")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return (
                      status === "Paid" ||
                      status === "paid" ||
                      status === "Approved" ||
                      status === "approved"
                    );
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("pending")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "pending"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.pending")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    const isUnderApproval = (
                      inv.buyerInvoicePayments || []
                    ).some((p) => p.amountPaid > 0 && p.isApproved === null);
                    return (
                      (status === "Pending" || status === "pending") &&
                      !isUnderApproval
                    );
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("partial-payment")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "partial-payment"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.partialPayment")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    const isUnderApproval = (
                      inv.buyerInvoicePayments || []
                    ).some((p) => p.amountPaid > 0 && p.isApproved === null);
                    return (
                      (status === "Partial Payment" ||
                        status === "partial-payment") &&
                      !isUnderApproval
                    );
                  }).length
                }
                )
              </button>

              <button
                onClick={() => setFilterStatus("cancelled")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "cancelled"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.cancelled")}(
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return status === "Cancelled" || status === "cancelled";
                  }).length
                }
                )
              </button>
            </nav>

            {/* Search Field */}
            <div className="flex items-center w-full lg:w-auto">
              <div className="relative w-full lg:w-64 xl:w-80">
                <input
                  type="text"
                  placeholder={translate("buyerInvoices.searchPlaceholder")}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 ${
                    isRTL ? "pr-9 sm:pr-10" : "pl-9 sm:pl-10"
                  } border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-xs sm:text-sm`}
                  value={searchInvoiceNumber || ""}
                  onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                />
                <svg
                  className={`absolute ${
                    isRTL ? "right-2.5 sm:right-3" : "left-2.5 sm:left-3"
                  } top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchInvoiceNumber && (
                <button
                  onClick={() => setSearchInvoiceNumber("")}
                  className="ml-2 p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Invoices List */}
        <div className="space-y-3 sm:space-y-4">
          {paginatedInvoices.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                {translate("buyerInvoices.messages.noInvoicesFound")}
              </h3>
              <p className="text-sm sm:text-base text-gray-500 px-4">
                {filterStatus === "all"
                  ? translate("buyerInvoices.messages.noInvoicesYet")
                  : translate(
                      "buyerInvoices.messages.noFilteredInvoices",
                    ).replace(
                      "{status}",
                      translate(`buyerInvoices.filters.${filterStatus}`),
                    )}
              </p>
            </div>
          ) : (
            paginatedInvoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              return (
                <div
                  key={invoice.id}
                  className="bg-white border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow"
                >
                  {/* Mobile Layout */}
                  <div className="block lg:hidden">
                    {/* Header with Status */}
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-center space-x-2 sm:space-x-2.5 flex-1 min-w-0">
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                        <h3 className="text-sm sm:text-base font-semibold text-black truncate">
                          {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                          {invoice.id}{invoice.isGatepassRenewal && (<span className="ml-2 text-xs font-medium text-orange-600">Gate Pass Renewal Fee</span>)}
                        </h3>
                      </div>
                      <span
                        className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${status.color}`}
                      >
                        {/* {invoice?.invoicePaymentStatus?.name} */}
                        {status.text}
                      </span>
                    </div>

                    {/* Vehicle Image and Basic Info */}
                    <div className="flex items-start space-x-3 sm:space-x-4 mb-3 sm:mb-4">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.images?.[0]
                          ?.imageUrl && (
                          <img
                            src={
                              invoice.buyerInvoiceDetails[0].vehicle.images[0]
                                .imageUrl || placeholder
                            }
                            alt="Vehicle"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-black leading-tight break-words">
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.name ||
                            "-"}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                          {translate("buyerInvoices.labels.lotNo")}{" "}
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                            ?.lotNumber || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Amount and Date Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1.5">
                          {translate("buyerInvoices.labels.totalAmount")}
                        </p>
                        <p className="text-sm sm:text-base font-semibold text-[#f64f2d]">
                          {formatPrice(invoice?.totalAmount)}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1.5">
                          {translate("buyerInvoices.labels.paidAmount")}
                        </p>
                        <p className="text-sm sm:text-base font-semibold text-green-700">
                          {formatPrice(
                            invoice?.totalAmount - invoice?.remainingAmount,
                          )}
                        </p>
                      </div>
                      {/* <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1.5">
                          {translate("buyerInvoices.labels.remainingAmount") ||
                            "Pending Amount"}
                        </p>
                        <p className="text-sm sm:text-base font-semibold text-red-600">
                          {formatPrice(invoice?.remainingAmount)}
                        </p>
                      </div>
                      {getUnderApprovalAmount(invoice) > 0 && (
                        <div className="bg-blue-50 rounded-lg p-2.5 sm:p-3 col-span-1 sm:col-span-2">
                          <p className="text-xs sm:text-sm text-blue-600 mb-1.5">
                            {translate("buyerInvoices.status.underApproval") ||
                              "Under Approval Amount"}
                          </p>
                          <p className="text-sm sm:text-base font-semibold text-blue-700">
                            {formatPrice(getUnderApprovalAmount(invoice))}
                          </p>
                        </div>
                      )} */}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4">
                      <p className="text-xs sm:text-sm text-gray-500 mb-1.5">
                        {translate("buyerInvoices.labels.invoiceDate")}
                      </p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900">
                        {invoice?.createdAt
                          ? new Date(invoice.createdAt).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              },
                            )
                          : "-"}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 sm:gap-3">
                      {/* Pay Now button for pending/partial payment invoices */}
                      {(status.text ===
                        translate("buyerInvoices.status.pending") ||
                        status.text ===
                          translate("buyerInvoices.status.pending") ||
                        status.text === "Pending" ||
                        status.text === "pending" ||
                        status.text ===
                          translate("buyerInvoices.status.partialPayment") ||
                        status.text === "Partial Payment" ||
                        status.text === "partial-payment") && (
                        <button
                          onClick={() => handlePayNow(invoice)}
                          disabled={(invoice.buyerInvoicePayments || []).some(
                            (p) => p.amountPaid > 0 && p.isApproved === null,
                          )}
                          className={`w-full bg-orange-500 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg transition-colors text-sm sm:text-base font-medium shadow-sm ${
                            (invoice.buyerInvoicePayments || []).some(
                              (p) => p.amountPaid > 0 && p.isApproved === null,
                            )
                              ? "opacity-50 cursor-not-allowed bg-orange-300"
                              : "hover:bg-orange-600"
                          }`}
                        >
                          {translate("buyerInvoices.buttons.payNow")}
                        </button>
                      )}

                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className={`flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base font-medium shadow-sm ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <Eye
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            isRTL ? "ml-2" : "mr-2"
                          }`}
                        />
                        {translate("buyerInvoices.buttons.viewDetails")}
                      </button>
                      {/* //mondy fix receipt show  */}
{canViewReceipts(invoice) && (
                      <button
                        onClick={() => handleViewReceipts(invoice)}
                        className={`flex items-center justify-centerpx-4 sm:px-5 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium shadow-sm ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <Eye
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? "ml-2" : "mr-2"}`}
                        />
                        View Receipts
                      </button>
)}
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden lg:block">
                    <div className="flex items-start justify-between">
                      {/* Invoice Details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <h3 className="text-base font-semibold text-black">
                              {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                              {invoice.id}{invoice.isGatepassRenewal && (<span className="ml-2 text-xs font-medium text-orange-600">Gate Pass Renewal Fee</span>)}
                            </h3>
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}
                          >
                            {/* {invoice?.invoicePaymentStatus?.name} */}
                            {status.text}
                          </span>
                        </div>

                        <div className="mb-3">
                          <div className="grid grid-cols-12 gap-4 items-center">
                            {/* Vehicle Image */}
                            <div className="col-span-1 flex justify-start">
                              <div className="w-12 h-12 bg-gray-200 rounded-md overflow-hidden flex-shrink-0">
                                {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                  ?.images?.[0]?.imageUrl && (
                                  <img
                                    src={
                                      invoice.buyerInvoiceDetails[0].vehicle
                                        .images[0].imageUrl || placeholder
                                    }
                                    alt="Vehicle"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                            </div>

                            {/* Vehicle Name */}
                            <div className="col-span-3 text-left">
                              <p className="text-xs font-semibold text-black">
                                {
                                  invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                    ?.name
                                }
                              </p>
                              <p className="text-xs font-semibold text-gray-500">
                                {translate("buyerInvoices.labels.lotNo")}{" "}
                                {
                                  invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                    ?.lotNumber
                                }
                              </p>
                            </div>

                            {/* Paid Amount */}
                            <div className="col-span-2 text-center">
                              <p className="text-xs text-gray-500">
                                {translate("buyerInvoices.labels.paidAmount")}
                              </p>
                              <p className="text-xs font-semibold text-green-700">
                                {formatPrice(
                                  invoice?.totalAmount -
                                    invoice?.remainingAmount,
                                )}
                              </p>
                            </div>

                            {/* Total Amount */}
                            <div className="col-span-2 text-center">
                              <p className="text-xs text-gray-500">
                                {translate("buyerInvoices.labels.totalAmount")}
                              </p>
                              <p className="text-xs font-semibold text-[#f64f2d]">
                                {formatPrice(invoice?.totalAmount)}
                              </p>
                            </div>
                            {/* Pending Amount *
                            <div className="col-span-1 text-center">
                              <p className="text-xs text-gray-500">
                                {translate(
                                  "buyerInvoices.labels.remainingAmount",
                                ) || "Pending"}
                              </p>
                              <p className="text-xs font-semibold text-red-600">
                                {formatPrice(invoice?.remainingAmount)}
                              </p>
                            </div>

                            {getUnderApprovalAmount(invoice) > 0 && (
                              <div className="col-span-1 text-center">
                                <p className="text-xs text-gray-500">
                                  {translate(
                                    "buyerInvoices.status.underApproval",
                                  ) || "Under Approval"}
                                </p>
                                <p className="text-xs font-semibold text-blue-600">
                                  {formatPrice(getUnderApprovalAmount(invoice))}
                                </p>
                              </div>
                            )} */}

                            {/* Invoice Date */}
                            <div className="col-span-1 text-center">
                              <p className="text-xs text-gray-500">
                                {translate("buyerInvoices.labels.invoiceDate")}
                              </p>
                              <p className="text-xs font-medium text-gray-900">
                                {invoice?.createdAt
                                  ? new Date(
                                      invoice.createdAt,
                                    ).toLocaleDateString(undefined, {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : ""}
                              </p>
                            </div>

                            {/* Empty space for alignment on large screens */}
                            <div className="col-span-2"></div>
                          </div>
                        </div>

                        {/* Vehicle Information */}
                        {invoice?.vehicle && (
                          <div className="bg-gray-50 rounded-md p-3">
                            <h4 className="font-medium text-gray-900 mb-1 text-sm">
                              {translate("buyerInvoices.labels.vehicleDetails")}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                              <div>
                                <span className="text-gray-500">
                                  {translate("buyerInvoices.labels.vehicle")}
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-2" : "ml-2"
                                  } font-medium`}
                                >
                                  {invoice?.vehicle?.name}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">
                                  {translate("buyerInvoices.labels.lotNumber")}
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-2" : "ml-2"
                                  } font-medium`}
                                >
                                  {invoice?.vehicle?.lotNumber}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-1 ml-4">
                        {/* Pay Now button for pending/partial payment invoices */}
                        {(status.text ===
                          translate("buyerInvoices.status.pending") ||
                          status.text ===
                            translate("buyerInvoices.status.pending") ||
                          status.text === "Pending" ||
                          status.text === "pending" ||
                          status.text ===
                            translate("buyerInvoices.status.partialPayment") ||
                          status.text === "Partial Payment" ||
                          status.text === "partial-payment") && (
                          <button
                            onClick={() => handlePayNow(invoice)}
                            disabled={(invoice.buyerInvoicePayments || []).some(
                              (p) => p.amountPaid > 0 && p.isApproved === null,
                            )}
                            className={`w-full text-white px-3 py-1.5 rounded-md transition-colors text-sm ${
                              (invoice.buyerInvoicePayments || []).some(
                                (p) =>
                                  p.amountPaid > 0 && p.isApproved === null,
                              )
                                ? "bg-orange-300 cursor-not-allowed"
                                : "bg-orange-500 hover:bg-orange-600"
                            }`}
                          >
                            <span>
                              {translate("buyerInvoices.buttons.payNow")}
                            </span>
                          </button>
                        )}

                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className={`flex items-center justify-center px-3 py-1.5 bg-[#f64f2d] text-white rounded-md hover:bg-orange-600 transition-colors text-sm ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <Eye
                            className={`w-4 h-4 ${isRTL ? "ml-1" : "mr-1"}`}
                          />
                          <span>
                            {translate("buyerInvoices.buttons.viewDetails")}
                          </span>
                        </button>
                        {/* //mondy fix receipt show  */}
{canViewReceipts(invoice) && (
                        <button
                          onClick={() => handleViewReceipts(invoice)}
                          className={`flex items-center justify-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <Eye
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                          />
                          View Receipts
                        </button>
)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 gap-3 sm:gap-0">
            <div
              className={`text-xs sm:text-sm text-gray-700 text-center ${
                isRTL ? "sm:text-right" : "sm:text-left"
              }`}
            >
              {translate("buyerInvoices.pagination.showing")} {startIndex + 1}{" "}
              {translate("buyerInvoices.pagination.to")}{" "}
              {Math.min(endIndex, filteredInvoices.length)}{" "}
              {translate("buyerInvoices.pagination.of")}{" "}
              {filteredInvoices.length}{" "}
              {translate("buyerInvoices.pagination.invoices")}
            </div>
            <div
              className={`flex items-center ${
                isRTL ? "space-x-reverse" : "space-x-1"
              } sm:space-x-2`}
            >
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate("buyerInvoices.pagination.previous")}
              </button>

              <div className="flex space-x-1 overflow-x-auto">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md flex-shrink-0 ${
                        currentPage === page
                          ? "bg-[#f64f2d] text-white"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate("buyerInvoices.pagination.next")}
              </button>
            </div>
          </div>
        )}
{/* monday fix receipt add */}
        {showReceiptsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-full max-w-3xl rounded-xl p-5 relative">
              {/* Close Button */}
              <button
                onClick={closeReceiptsModal}
                className="absolute top-3 right-3 text-gray-600 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="text-lg font-semibold mb-4">Payment Receipts</h2>

              {selectedReceipts.length === 0 ? (
                <p className="text-gray-500">No receipts found</p>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {selectedReceipts.map((payment) => (
                    <div key={payment.id} className="border rounded-lg p-3">
                      <div className="flex justify-between mb-2">
                        <p className="text-sm font-medium">
                          Amount Paid: {formatAmount(payment.amountPaid)}
                        </p>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            payment.isApproved
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {payment.isApproved === true
                            ? "Approved"
                            : payment.isApproved === false
                              ? "Rejected"
                              : "Pending"}
                        </span>
                      </div>

                      {payment.imageUrl && (
                        <a
                          href={payment.imageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 text-sm underline break-all"
                        >
                          View Receipt File
                        </a>
                      )}

                      {/* If it's PDF/image preview */}
                      {payment.imageUrl?.includes(".pdf") ? (
                        <iframe
                          src={payment.imageUrl}
                          className="w-full h-64 mt-2 border"
                        />
                      ) : (
                        payment.imageUrl && (
                          <img
                            src={payment.imageUrl}
                            className="w-full h-64 object-contain mt-2 border"
                          />
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
{/* monday fix end */}
        {/* Invoice Payment Modal */}
        {showInvoiceModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {translate("buyerInvoices.labels.paymentHistory")} #
                  {selectedInvoice?.id}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                {/* Invoice Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-500 mb-1.5 sm:mb-2">
                      {translate("buyerInvoices.labels.totalAmount")}
                    </h4>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {formatPrice(selectedInvoice?.totalAmount)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-500 mb-1.5 sm:mb-2">
                      {translate("buyerInvoices.labels.remainingAmount")}
                    </h4>
                    <p className="text-xl sm:text-2xl font-bold text-red-600">
                      {formatPrice(selectedInvoice?.remainingAmount)}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-blue-600 mb-1.5 sm:mb-2">
                      {translate("buyerInvoices.status.underApproval") ||
                        "Under Approval"}
                    </h4>
                    <p className="text-xl sm:text-2xl font-bold text-blue-700">
                      {formatPrice(
                        selectedInvoice.buyerInvoicePayments?.reduce(
                          (sum, p) =>
                            p.isApproved === null ? sum + p.amountPaid : sum,
                          0,
                        ) || 0,
                      )}
                    </p>
                  </div>
                </div>

                {/* Payment History */}
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                      {translate("buyerInvoices.labels.paymentHistory")}
                    </h4>
                  </div>

                  {/* Payment History Table */}
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.paymentId")}
                            </th>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.paidAmount")}
                            </th>

                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.status")}
                            </th>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.invoiceDate")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedInvoice ? (
                            <tr>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                                #{selectedInvoice?.invoicePaymentStatus?.id}
                              </td>

                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                                {formatPrice(
                                  selectedInvoice.totalAmount -
                                    selectedInvoice.remainingAmount,
                                )}
                              </td>

                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                                    getInvoiceStatus(selectedInvoice).color
                                  }`}
                                >
                                  {selectedInvoice.invoicePaymentStatus?.name}
                                </span>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                                {formatDate(selectedInvoice.createdAt)}
                              </td>
                            </tr>
                          ) : (
                            <tr>
                              <td
                                colSpan="4"
                                className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-500"
                              >
                                {translate(
                                  "buyerInvoices.messages.noPaymentHistory",
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end p-4 sm:p-6 border-t">
                <button
                  onClick={handleCloseModal}
                  className="px-4 sm:px-6 py-2 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base"
                >
                  {translate("buyerInvoices.buttons.close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Popup */}
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType="invoice"
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BuyerPayment;
